import NextLink from "next/link"
import {
  Button,
  Heading,
  Text,
  Link,
  Input,
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Center,
  useToast,
} from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import { useTranslations } from "use-intl"
import { PageSEO } from "@components/HeadSEO"
import supabase from "@utils/initSupabase"

const ResetPassword = (): JSX.Element => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors: formErrors, isSubmitting },
  } = useForm()
  const toast = useToast()
  const t = useTranslations()

  const submitResetRequest = async ({ email = "" }: { email?: string }) => {
    const { data, error } = await supabase.auth.api.resetPasswordForEmail(email)

    if (error) {
      toast({
        title: String(t("resetpassword.toastErrorTitle")),
        description: String(t("resetpassword.toastErrorMsg")),
        status: "error",
        duration: 9000,
        isClosable: true,
      })
    }

    if (data) {
      toast({
        title: String(t("resetpassword.toastSuccessTitle")),
        description: String(t("resetpassword.toastSuccessMsg")),
        status: "success",
        duration: 9000,
        isClosable: true,
      })
    }

    reset({ password: "" })
  }

  return (
    <>
      <PageSEO
        title={String(t("resetpassword.pageTitle"))}
        description={String(t("resetpassword.pageDescription"))}
      />
      <Center>
        <Box
          maxW="30rem"
          w="100%"
          mt={[6, null, 12, 16, 20]}
          p="6"
          borderRadius="lg"
          overflow="hidden"
        >
          <Heading variant="contentHeading1">
            {t("resetpassword.resetTitle")}
          </Heading>
          <Text mt="4">{t("resetpassword.resetDescription")}</Text>
          <form onSubmit={handleSubmit(submitResetRequest)}>
            <FormControl isInvalid={!!formErrors.email}>
              <FormLabel htmlFor="email" mt="4">
                {t("resetpassword.formEmailLabel")}
              </FormLabel>
              <Input
                id="email"
                type="email"
                placeholder={String(t("resetpassword.formEmailPlaceholder"))}
                focusBorderColor="orange"
                {...register("email", {
                  required: "Kirjoita sähköposti",
                  pattern: {
                    value: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, // eslint-disable-line
                    message: String(t("resetpassword.formEmailErrorMsg")),
                  },
                })}
              />
              <FormErrorMessage color="white">
                {typeof formErrors.email?.message === "string"
                  ? formErrors.email.message
                  : null}
              </FormErrorMessage>
            </FormControl>

            <Button
              type="submit"
              size="md"
              w="100%"
              mt="8"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t("resetpassword.submitLoading")
                : t("resetpassword.submit")}
            </Button>
            <NextLink href={String(t("navigation.signInUrl"))} passHref>
              <Link textAlign="center" mt="4" display="block">
                {t("navigation.signInLabel")}
              </Link>
            </NextLink>
          </form>
        </Box>
      </Center>
    </>
  )
}

export default ResetPassword
